<template>
  <qr-list-table
    :title="companyName"
    :items="employees"
    :loading="loading"
    :headers="headers"
    :back-route="{ name: 'firma-id-korisnici', params: { id: companyId } }"
    :back-label="$t('back_to_users')"
    :show-print-settings="true"
    :items-per-page-label="$t('employees_per_page')"
  >
    <template #toolbar-filters>
      <v-col cols="auto" v-if="hasChildCompanies">
        <autocomplete
          v-model="selectedChildCompanies"
          :items="parentCompanies || []"
          multiple
          item-text="name"
          item-value="id"
          @input="filterByChildCompanies"
          :label="$t('select_company')"
          hide-details
          color="white"
          chips
          small-chips
          style="min-width: 300px"
        >
          <v-btn
            color="primary"
            slot="prepend-item"
            @click="toggleAllCompanies"
            small
            class="mx-4"
          >
            {{ $t("display_all") }}
          </v-btn>
        </autocomplete>
      </v-col>
    </template>
    <template #mobile-filters v-if="hasChildCompanies">
      <mobile-filters
        :filters="mobileFilterConfig"
        @update:filter="handleMobileFilterUpdate"
        @apply="filterByChildCompanies"
        @reset="resetFilters"
      >
        <template #filter-company>
          <autocomplete
            v-model="selectedChildCompanies"
            :items="parentCompanies || []"
            multiple
            item-text="name"
            item-value="id"
            :label="$t('select_company')"
            hide-details
            chips
            small-chips
          />
        </template>
      </mobile-filters>
    </template>
  </qr-list-table>
</template>

<script>
import QrListTable from "@/components/qr/QrListTable.vue";
import MobileFilters from "@/components/global/MobileFilters.vue";
import companyService from "@/services/company";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  name: "CompanyQrList",
  mixins: [hasSnackbar],
  components: {
    QrListTable,
    MobileFilters,
  },
  data: () => ({
    companyName: "",
    employees: [],
    allEmployees: [],
    loading: false,
    parentCompanies: [],
    selectedChildCompanies: [],
    children: [],
  }),
  created() {
    // Get route params if passed
    if (this.$route.params.children) {
      this.children = this.$route.params.children;
    }
    if (this.$route.params.parentCompanies) {
      this.parentCompanies = this.$route.params.parentCompanies;
    }

    this.loadCompanyUsers();
  },
  methods: {
    async loadCompanyUsers() {
      this.loading = true;
      try {
        const res = await companyService.getCompanyUsers(this.companyId);
        this.companyName = res.data.name;

        const users = (res.data.users || []).map((user) => {
          // Ensure qrCode is a string
          if (typeof user.qrCode === "object" && user.qrCode !== null) {
            user.qrCode = JSON.stringify(user.qrCode);
          }
          return user;
        });

        this.employees = users;
        this.allEmployees = [...users];

        // Update parentCompanies with users
        this.parentCompanies.forEach((company) => {
          if (company.id === res.data.id) {
            company.users = res.data.users;
          }
        });
      } catch (error) {
        console.error("Error loading company users:", error);
        this.showErrorSnackbar(this.$t("error_loading_company_users"));
      } finally {
        this.loading = false;
      }
    },

    toggleAllCompanies() {
      if (this.selectedChildCompanies.length > 0) {
        this.selectedChildCompanies = [];
      } else {
        this.selectedChildCompanies = this.parentCompanies.map((c) => c.id);
      }
      this.filterByChildCompanies();
    },

    filterByChildCompanies() {
      if (this.selectedChildCompanies.length === 0) {
        this.employees = this.allEmployees;
        return;
      }

      const filteredUsers = [];
      this.parentCompanies.forEach((company) => {
        if (company.users && company.users.length > 0) {
          company.users.forEach((user) => {
            if (this.selectedChildCompanies.includes(user.pivot?.company_id)) {
              filteredUsers.push(user);
            }
          });
        }
      });

      this.employees = filteredUsers;
    },

    handleMobileFilterUpdate({ key, value }) {
      if (key === "company") {
        this.selectedChildCompanies = value;
      }
    },

    resetFilters() {
      this.selectedChildCompanies = [];
      this.employees = this.allEmployees;
    },
  },
  computed: {
    companyId() {
      if (this.$store.state.company) {
        return this.$store.state.company.id;
      }
      return this.$route.params.id;
    },

    hasChildCompanies() {
      return (
        this.children &&
        this.children.length > 0 &&
        this.parentCompanies.length > 0 &&
        this.parentCompanies.find((c) => c.parent_id === 0)?.id === this.companyId
      );
    },

    headers() {
      return [
        {
          text: "Id",
          value: "id",
        },
        {
          text: this.$t("external_code"),
          value: "external_code",
        },
        {
          text: this.$t("name"),
          value: "name",
        },
        {
          text: this.$t("last_name"),
          value: "lastname",
        },
        {
          text: this.$t("ek"),
          value: "email",
        },
        {
          text: this.$t("role"),
          value: "type",
          align: "center",
        },
        {
          text: this.$t("qr_code"),
          value: "qrCode",
          sortable: false,
          align: "center",
        },
      ];
    },
    mobileFilterConfig() {
      return {
        company: {
          type: "custom",
          label: this.$t("select_company"),
          value: this.selectedChildCompanies,
          visible: this.selectedChildCompanies.length > 0,
          chipText:
            this.selectedChildCompanies.length > 0
              ? `${this.selectedChildCompanies.length} ${this.$t("selected")}`
              : "",
        },
      };
    },
  },
};
</script>
